import {
  PointOfInterest,
  PointOfInterestTemplate,
  MarkerLabelFieldTypeEnum,
  MarkerLabelField,
  UnitsEnum,
} from '@/__generated__/graphql';
import { enumToString } from '@/utils/string';
import {
  NOT_APPLICABLE_LABEL,
  BLISTER_TYPE,
  BLISTER_HEIGHT_LABEL,
  PIT_DEPTH_LABEL,
  PAINT_BLOCK_LABEL,
  UNCERTAIN_PROFILE_LABEL,
} from '@/constants';
import { formatEnumForDisplay, titleCase, convertTimeStampToDate } from '@/utils';
import { format } from 'date-fns';
import { SC_CATEGORY_COLORS, ALARM_LEVEL_COLORS, DAMAGE_CLASS_COLOR_MAP } from '@/theme/colors';
import { formatPercentage, localiseMeasurement } from '@/utils/unitSystem';
import { UnitSystemEnum } from '@/__generated__/graphql';

const getFieldValue = (
  field: MarkerLabelField,
  pointOfInterest: PointOfInterest,
  selectedPoiTemplate?: Partial<PointOfInterestTemplate> | null,
  unitSystemParam?: UnitSystemEnum,
  getPoiStatusLabel?: (status: string) => string
): { name: string; value: string | number | undefined; color?: string } => {
  const { key, type } = field;
  let value: string | number | undefined = undefined;
  let name = enumToString(key);
  let color: string | undefined = undefined;

  // Set specific labels for fields based on GeneralTab and PointOfInterestInfo components
  if (type === MarkerLabelFieldTypeEnum.Blister) {
    if (key === 'maxHeight') {
      name = pointOfInterest.type === BLISTER_TYPE ? BLISTER_HEIGHT_LABEL : PIT_DEPTH_LABEL;
    } else if (key === 'wallLoss') {
      name = 'Estimated Wall Loss';
    } else if (key === 'residualCorrosionAllowance') {
      name = 'Residual Corrosion Allowance';
    } else if (key === 'residualThickness') {
      name = 'Residual Thickness';
    } else if (key === 'measurementConfidence') {
      name = 'Measurement Confidence';
    } else if (key === 'distanceConfidence') {
      name = 'Distance Confidence';
    }
  } else if (type === MarkerLabelFieldTypeEnum.PointOfInterest) {
    // Use the same labels as in GeneralTab
    switch (key) {
      case 'poiId':
        name = 'POI ID';
        break;
      case 'name':
        name = 'Name';
        break;
      case 'paintRegion':
        name = PAINT_BLOCK_LABEL;
        break;
      case 'assembly':
        name = 'Component';
        break;
      case 'status':
        name = 'Status';
        break;
      case 'visibility':
        name = 'Access Control';
        break;
      case 'assignee':
        name = 'Assigned To';
        break;
      case 'comment':
        name = 'Comment';
        break;
      case 'accessibility':
        name = 'Accessibility';
        break;
      case 'createdAt':
        name = 'Date Created';
        break;
      case 'dueDate':
        name = 'Due Date';
        break;
      case 'workpack':
        name = 'Workpack';
        break;
      case 'createdByUser.email':
        name = 'Created By';
        break;
      case 'deckRelativeHeight':
        name = 'POI Height from Deck';
        break;
      case 'viewpointRelativeHeight':
        name = 'POI Height from Viewpoint';
        break;
    }
  }

  // Use the provided unit system or default to Metric
  const unitSystem = unitSystemParam || UnitSystemEnum.Metric;

  switch (type) {
    case MarkerLabelFieldTypeEnum.PointOfInterest:
      // Format PointOfInterest fields similar to GeneralTab
      if (key === 'status' && pointOfInterest.status) {
        value = getPoiStatusLabel
          ? getPoiStatusLabel(pointOfInterest.status)
          : pointOfInterest.status;
      } else if (key === 'visibility' && pointOfInterest.visibility) {
        value = titleCase(pointOfInterest.visibility);
      } else if (key === 'accessibility' && pointOfInterest.accessibility) {
        value = formatEnumForDisplay(pointOfInterest.accessibility);
      } else if (key === 'dueDate' && pointOfInterest.dueDate) {
        const convertedDate = convertTimeStampToDate(pointOfInterest.dueDate);
        value = convertedDate ? format(convertedDate, 'dd/MMM/yyyy') : NOT_APPLICABLE_LABEL;
      } else if (key === 'createdAt' && pointOfInterest.createdAt) {
        const convertedDate = convertTimeStampToDate(pointOfInterest.createdAt);
        value = convertedDate ? format(convertedDate, 'dd/MMM/yyyy') : NOT_APPLICABLE_LABEL;
      } else if (key === 'paintRegion' && pointOfInterest.paintRegion) {
        value = pointOfInterest.paintRegion.name;
      } else if (key === 'assembly' && pointOfInterest.assembly) {
        value = pointOfInterest.assembly.tagName;
      } else if (key === 'workpack' && pointOfInterest.workpack) {
        value = pointOfInterest.workpack.name;
      } else if (key === 'createdBy' && pointOfInterest.createdByUser) {
        value = pointOfInterest.createdByUser?.name ?? NOT_APPLICABLE_LABEL;
      } else if (key === 'deckRelativeHeight' && pointOfInterest.deckRelativeHeight) {
        value = localiseMeasurement({
          unitSystem,
          sourceUnit: UnitsEnum.Meter,
          value: pointOfInterest.deckRelativeHeight,
        });
      } else if (key === 'viewpointRelativeHeight' && pointOfInterest.viewpointRelativeHeight) {
        value = localiseMeasurement({
          unitSystem,
          sourceUnit: UnitsEnum.Meter,
          value: pointOfInterest.viewpointRelativeHeight,
        });
      } else {
        value = pointOfInterest[key as keyof PointOfInterest];
      }
      break;

    case MarkerLabelFieldTypeEnum.Blister:
      if (pointOfInterest.blister) {
        if (key === 'maxHeight') {
          value =
            localiseMeasurement({
              unitSystem,
              value: pointOfInterest.blister?.maxHeight || undefined,
              showUnitLabel: true,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              fallbackLabel: UNCERTAIN_PROFILE_LABEL,
            }) ?? UNCERTAIN_PROFILE_LABEL;
        }
        if (key === 'wallLoss') {
          const wallLossValue = pointOfInterest.blister?.wallLoss?.value;
          value =
            wallLossValue === null || wallLossValue === undefined
              ? NOT_APPLICABLE_LABEL
              : localiseMeasurement({
                  unitSystem,
                  value: wallLossValue,
                  showUnitLabel: true,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                }) +
                  (pointOfInterest.blister?.wallLoss?.percentage !== null &&
                  pointOfInterest.blister?.wallLoss?.percentage !== undefined
                    ? formatPercentage(pointOfInterest.blister.wallLoss.percentage)
                    : '');
        }
        if (key === 'residualCorrosionAllowance') {
          const rcaValue = pointOfInterest.blister?.residualCorrosionAllowance?.value;
          value =
            rcaValue === null || rcaValue === undefined
              ? NOT_APPLICABLE_LABEL
              : localiseMeasurement({
                  unitSystem,
                  value: rcaValue,
                  showUnitLabel: true,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                }) +
                  (pointOfInterest.blister?.residualCorrosionAllowance?.percentage !== null &&
                  pointOfInterest.blister?.residualCorrosionAllowance?.percentage !== undefined
                    ? formatPercentage(pointOfInterest.blister.residualCorrosionAllowance.percentage)
                    : '');
        }
        if (key === 'residualThickness') {
          const rtValue = pointOfInterest.blister?.residualThickness?.value;
          value =
            rtValue === null || rtValue === undefined
              ? NOT_APPLICABLE_LABEL
              : localiseMeasurement({
                  unitSystem,
                  value: rtValue,
                  showUnitLabel: true,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                }) +
                  (pointOfInterest.blister?.residualThickness?.percentage !== null &&
                  pointOfInterest.blister?.residualThickness?.percentage !== undefined
                    ? formatPercentage(pointOfInterest.blister.residualThickness.percentage)
                    : '');
        }
        if (key === 'measurementConfidence') {
          value = pointOfInterest.blister?.measurementConfidence ?? NOT_APPLICABLE_LABEL;
        }
        if (key === 'distanceConfidence') {
          value = pointOfInterest.blister?.distanceConfidence ?? NOT_APPLICABLE_LABEL;
        }
      }
      break;

    case MarkerLabelFieldTypeEnum.TemplateField:
      if (selectedPoiTemplate?.fields && pointOfInterest.templateFieldData) {
        const templateField = selectedPoiTemplate.fields.find((field) => field.key === key);

        if (templateField) {
          const fieldValue = pointOfInterest.templateFieldData[key];

          if (fieldValue !== undefined) {
            value = fieldValue?.toString();
            name = templateField.name || enumToString(key);
          }
        }
      }
      break;

    default:
      value = NOT_APPLICABLE_LABEL;
  }

  // Add color mapping based on field key and value
  if (key === 'sc_category' && value) {
    color = SC_CATEGORY_COLORS[value as keyof typeof SC_CATEGORY_COLORS];
  } else if (key === 'damage_class' && value) {
    color = DAMAGE_CLASS_COLOR_MAP[value as keyof typeof DAMAGE_CLASS_COLOR_MAP];
  } else if (key === 'alarm_level' && value) {
    color = ALARM_LEVEL_COLORS[value as keyof typeof ALARM_LEVEL_COLORS];
  }

  return { name, value, color };
};

/**
 * Gets multiple field values based on annotation configuration
 * @param fields - Array of field configurations
 * @param pointOfInterest - The point of interest object
 * @param selectedPoiTemplate - The selected point of interest template
 * @param unitSystem - The unit system to use for conversion
 * @returns An array of objects containing field names and values
 */
export const getFieldValues = (
  fields: MarkerLabelField[],
  pointOfInterest: PointOfInterest,
  selectedPoiTemplate?: Partial<PointOfInterestTemplate> | null,
  unitSystem?: UnitSystemEnum,
  getPoiStatusLabel?: (status: string) => string
): Array<{ name: string; value: string | number | undefined; color?: string }> => {
  return fields.map((field) =>
    getFieldValue(field, pointOfInterest, selectedPoiTemplate, unitSystem, getPoiStatusLabel)
  );
};
